import { Fragment } from 'react';

import CheckIcon from 'shared/icons/Check';
import ClearIcon from 'shared/icons/Clear';
import {
  CardContainer,
  Card,
  CardTitle,
  Item,
  List,
  ListContainer,
  Label,
  ListTitle,
  Value,
} from 'shared/components/styled/Cases';
import type { RenovationType } from 'shared/models/space-renovation-requests';
import useTranslation from 'shared/hooks/useTranslation';
import formatDate from 'shared/utils/formatDate';
import safeParseHtml from 'shared/utils/safeParseHtml';
import { CaseStateCode, CaseStatuses, RenovationExecutor } from 'utils/enums';

import type { UserSpaceRenovationRequest } from '../../../types';

const toFormatedDate = (str: string | null) =>
  str ? formatDate(new Date(str)) : null;

const parseDescription = (description: string | null) => {
  if (!description) return null;

  return safeParseHtml(description.replaceAll('\n', '<br />'));
};

type RepairCaseInfoProps = {
  repairCase: UserSpaceRenovationRequest;
};

const RepairCaseInfo = ({ repairCase }: RepairCaseInfoProps) => {
  const { t } = useTranslation();

  const renderWorkerInfo = (renovationType: RenovationType) => {
    switch (renovationType.ExecutorType?.Value) {
      case RenovationExecutor.Organization:
        return `${renovationType.OrganizationName}, ${renovationType.BusinessId}`;
      case RenovationExecutor.ForeignCompany:
        return `${renovationType.OrganizationName}, ${renovationType.ForeignRegistrationNumber}`;
      case RenovationExecutor.Person:
      case RenovationExecutor.PortalUser:
        return (
          renovationType.FullName ??
          `${renovationType.FirstName} ${renovationType.LastName}`
        );
      default:
        return null;
    }
  };

  const showSupervisorInfo =
    repairCase.Supervisor &&
    (repairCase.State?.Value === CaseStateCode.Resolved ||
      repairCase.Status?.Value === CaseStatuses.Approved);

  return (
    <CardContainer>
      <CardTitle>{t('Information')}</CardTitle>
      <Card>
        <ListContainer>
          <List>
            <Item>
              <Label>{t('##attribute#incident#createdon')}</Label>
              <Value>{toFormatedDate(repairCase.CreatedOn)}</Value>
            </Item>
          </List>
        </ListContainer>

        <ListContainer>
          <ListTitle>Kohde</ListTitle>
          <List>
            <Item>
              <Label>Ilmoittaja</Label>
              <Value>{repairCase.ContactPerson?.Name}</Value>
            </Item>
            <Item>
              <Label>Organisaation nimi</Label>
              <Value>{repairCase.Cooperative?.Name}</Value>
            </Item>
            <Item>
              <Label>Rakennuksen osoite</Label>
              <Value>{repairCase.BuildingAddress}</Value>
            </Item>
            <Item>
              <Label>Tila</Label>
              <Value>{repairCase.Space?.Name}</Value>
            </Item>
          </List>
        </ListContainer>

        <ListContainer>
          <ListTitle>Muutostyön tiedot</ListTitle>
          <List>
            {repairCase.RenovationTypes.map((renovationType) => (
              <Fragment key={renovationType.Id}>
                <Item>
                  <Label>Muutostyön tyyppi</Label>
                  <Value>{renovationType.Type?.Label}</Value>
                </Item>
                <Item>
                  <Label>Työnsuorittaja</Label>
                  <Value>{renderWorkerInfo(renovationType)}</Value>
                </Item>
              </Fragment>
            ))}
            <Item>
              <Label>Arvioitu ajoitus</Label>
              <Value>{repairCase.EstimatedTiming}</Value>
            </Item>
            <Item>
              <Label>Näytä isännöitsijäntodistuksella</Label>
              <Value>
                {repairCase.IncludeInCooperativeReport ? (
                  <CheckIcon color="success" />
                ) : (
                  <ClearIcon color="error" />
                )}
              </Value>
            </Item>
          </List>
        </ListContainer>

        {showSupervisorInfo && (
          <ListContainer>
            <ListTitle>{t('SupervisorSectionHeader')}</ListTitle>
            <List>
              <Item>
                <Label>{t('Name')}</Label>
                <Value>{repairCase.Supervisor?.Name}</Value>
              </Item>
              <Item>
                <Label>{t('Email')}</Label>
                <Value>{repairCase.Supervisor?.Email}</Value>
              </Item>
              <Item>
                <Label>{t('MobilePhone')}</Label>
                <Value>{repairCase.Supervisor?.MobilePhone}</Value>
              </Item>
            </List>
          </ListContainer>
        )}

        <ListContainer>
          <ListTitle>Kuvaus</ListTitle>
          <List>
            <Item spacing={0.5}>
              <Value fullWidth align="left">
                {repairCase.Description &&
                  parseDescription(repairCase.Description)}
              </Value>
            </Item>
            <Item direction="column" spacing={0.5}>
              <Label>Ratkaisun kuvaus</Label>
              <Value align="left" fullWidth>
                {repairCase.ResolutionDescription &&
                  parseDescription(repairCase.ResolutionDescription)}
              </Value>
            </Item>
          </List>
        </ListContainer>
      </Card>
    </CardContainer>
  );
};

export default RepairCaseInfo;
